import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { resolveReadableHttpErrorMessage } from "../../../lib/http/errors";
import { financePaymentRequestQueryKey } from "../config";
import {
  recordFinancePaymentRequestPayment,
  uploadFinancePaymentReceipt,
  voidFinancePaymentRequestPayment,
} from "../repo/financePaymentRequestRepo";
import {
  emptyFinancePaymentRecordDraft,
  validateFinancePaymentRecordDraft,
  validateFinancePaymentRequestAttachment,
  validateFinancePaymentVoidReason,
} from "../service/financePaymentRequestService";
import type { FinancePaymentRecordDraft } from "../types";

/**
 * 登记已付表单：填本方付款账户、付款日期、银行流水号、备注，上传付款回单，提交。
 * 校验通过才提交；成功后定向刷新请款详情和请款记录。上传、提交时防重复点击；后端拦下原样显示原因。
 */
export function useFinancePaymentRecordForm(requestId: string) {
  const client = useQueryClient();
  const [draft, setDraft] = useState<FinancePaymentRecordDraft>(emptyFinancePaymentRecordDraft);
  const [errors, setErrors] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState("");
  const [serverError, setServerError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);

  return {
    draft,
    errors,
    uploadError,
    serverError,
    uploading,
    submitting,
    /** 改一个字段；改了就收起上一次的提示。 */
    setField<K extends keyof FinancePaymentRecordDraft>(key: K, value: FinancePaymentRecordDraft[K]) {
      setDraft((current) => ({ ...current, [key]: value }));
      setErrors([]);
      setServerError("");
    },
    /** 先在前端校验文件，再上传；上传成功的回单加进列表。 */
    async upload(file: File) {
      const error = validateFinancePaymentRequestAttachment(file);
      setUploadError(error);
      if (error || uploading) return;
      setUploading(true);
      try {
        const attachment = await uploadFinancePaymentReceipt(file);
        setDraft((current) => ({ ...current, attachments: [...current.attachments, attachment] }));
        setErrors([]);
      } catch (uploadFailure) {
        setUploadError(resolveReadableHttpErrorMessage(uploadFailure, "付款回单上传失败，请重试。"));
      } finally {
        setUploading(false);
      }
    },
    /** 从这次登记里拿掉一个回单（文件留在服务器上，不再绑定）。 */
    removeAttachment(attachmentId: string) {
      setDraft((current) => ({
        ...current,
        attachments: current.attachments.filter((item) => item.attachmentId !== attachmentId),
      }));
    },
    /** 校验通过才提交，成功返回真；后端拦下时原样显示原因，表单留着可改。 */
    async submit(): Promise<boolean> {
      if (submitLock.current || uploading) return false;
      const found = validateFinancePaymentRecordDraft(draft);
      setErrors(found);
      setServerError("");
      if (found.length > 0) return false;
      submitLock.current = true;
      setSubmitting(true);
      try {
        await recordFinancePaymentRequestPayment(requestId, draft);
        void client.invalidateQueries({ queryKey: financePaymentRequestQueryKey });
        return true;
      } catch (submitFailure) {
        setServerError(resolveReadableHttpErrorMessage(submitFailure, "登记失败，请稍后重试。"));
        return false;
      } finally {
        submitLock.current = false;
        setSubmitting(false);
      }
    },
  };
}

/** 作废这次登记：原因必填；成功后定向刷新请款详情和请款记录。提交时防重复点击；后端拦下原样显示原因。 */
export function useFinancePaymentVoidForm(requestId: string, paymentId: string) {
  const client = useQueryClient();
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);

  return {
    reason,
    error,
    submitting,
    /** 改原因；改了就收起上一次的提示。 */
    setReason(value: string) {
      setReason(value);
      setError("");
    },
    /** 原因不为空才提交，成功返回真。 */
    async submit(): Promise<boolean> {
      if (submitLock.current) return false;
      const found = validateFinancePaymentVoidReason(reason);
      setError(found);
      if (found) return false;
      submitLock.current = true;
      setSubmitting(true);
      try {
        await voidFinancePaymentRequestPayment(requestId, paymentId, reason);
        void client.invalidateQueries({ queryKey: financePaymentRequestQueryKey });
        return true;
      } catch (submitFailure) {
        setError(resolveReadableHttpErrorMessage(submitFailure, "作废失败，请稍后重试。"));
        return false;
      } finally {
        submitLock.current = false;
        setSubmitting(false);
      }
    },
  };
}
